import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import UpcomingClasses from './UpcomingClasses';


// Rendered from live selectedStudent state

export default function StudentDashboard() {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [viewDate, setViewDate] = useState(new Date());
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  


  const [penData, setPenData] = useState([]);
  const [penDataLoading, setPenDataLoading] = useState(false);
  const [timetableDate, setTimetableDate] = useState(new Date());

  const [homeworks, setHomeworks] = useState([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedHomeworkForSub, setSelectedHomeworkForSub] = useState(null);
  const [submissionFile, setSubmissionFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || "{}");
    } catch {
      return {};
    }
  }, []);
  const isStudentRole = loggedInUser.role === 'student';

  useEffect(() => {
    if (isStudentRole && loggedInUser.email) {
      setLoadingBatches(true);
      setLoadingStudents(true);
      axios.get(`${API_BASE_URL}/api/students/profile?email=${loggedInUser.email}`)
        .then(res => {
          const studentProfile = res.data;
          setSelectedBatch(studentProfile.batchIdStr || '');
          setSelectedStudent(studentProfile);
          if (studentProfile.batchIdStr) {
            setBatches([{ id: 'student_batch', batchIdStr: studentProfile.batchIdStr, name: 'Assigned Batch' }]);
          }
          setStudents([studentProfile]);
        })
        .catch(console.error)
        .finally(() => {
          setLoadingBatches(false);
          setLoadingStudents(false);
        });
    } else {
      fetchBatches();
    }
  }, [isStudentRole, loggedInUser.email]);

  useEffect(() => {
    if (isStudentRole) return; // Prevent fetching peers for security
    if (selectedBatch) {
      fetchStudentsByBatch(selectedBatch);
    } else {
      setStudents([]);
    }
  }, [selectedBatch, isStudentRole]);

  useEffect(() => {
    if (selectedStudent?.email) {
      if (selectedBatch) {
        fetchHomeworks(selectedBatch, selectedStudent.email);
      }
    } else {
      setHomeworks([]);
    }
  }, [selectedStudent, selectedBatch]);

  useEffect(() => {
    if (selectedStudent?.penid) {
      fetchPenData(selectedStudent.penid);
    } else {
      setPenData([]);
    }
  }, [selectedStudent]);

  const fetchPenData = (penId) => {
    setPenDataLoading(true);
    axios.get(`${API_BASE_URL}/api/dashboard/${penId}?filter=all`)
      .then(res => setPenData(res.data))
      .catch(console.error)
      .finally(() => setPenDataLoading(false));
  };

  const writingMetrics = useMemo(() => {
    if (!penData || penData.length === 0) return { wordsToday: 0, avgSession: 0 };
    
    const now = new Date();
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 1000;
    
    const minutesToday = penData
      .filter(d => d.dayStartS === todayUTC)
      .reduce((acc, d) => acc + (d.minutesUsed || 0), 0);
      
    const sessions = penData.filter(d => d.minutesUsed > 0);
    const avgMinutes = sessions.length > 0 
      ? sessions.reduce((acc, d) => acc + d.minutesUsed, 0) / sessions.length 
      : 0;

    return {
      wordsToday: Math.round(minutesToday * 15),
      avgSession: Math.round(avgMinutes)
    };
  }, [penData]);



  const fetchBatches = () => {
    setLoadingBatches(true);
    axios.get(API_BASE_URL + '/api/batches?t=' + Date.now())
      .then(res => setBatches(res.data))
      .catch(console.error)
      .finally(() => setLoadingBatches(false));
  };

  const fetchStudentsByBatch = (batchIdStr) => {
    setLoadingStudents(true);
    axios.get(`${API_BASE_URL}/api/students/by-batch/${batchIdStr}?t=${Date.now()}`)
      .then(res => {
        if (Array.isArray(res.data)) {
          setStudents(res.data);
        } else {
          console.error("API did not return an array for students by batch:", res.data);
          setStudents([]);
        }
      })
      .catch(err => {
        console.error("Failed to fetch students by batch:", err);
        setStudents([]);
      })
      .finally(() => setLoadingStudents(false));
  };



  const fetchHomeworks = (batchId, email) => {
    axios.get(`${API_BASE_URL}/api/homework/batch/${batchId}?studentEmail=${email}`)
      .then(res => setHomeworks(res.data))
      .catch(console.error);
  };

  const handleSubmitHomework = async (e) => {
    e.preventDefault();
    if (!submissionFile || !selectedHomeworkForSub) return;
    setSubmitting(true);
    try {
        // Step 1: Upload File
        const formData = new FormData();
        formData.append("file", submissionFile);
        formData.append("title", `Submission - ${selectedHomeworkForSub.homework.title}`);
        formData.append("subject", "Submission");
        formData.append("type", "SUBMISSION");
        
        const fileRes = await axios.post(`${API_BASE_URL}/api/files/upload`, formData);
        const fileId = fileRes.data.id;

        // Step 2: Create Submission
        const submissionData = {
            studentEmail: selectedStudent.email,
            fileId: fileId
        };
        await axios.post(`${API_BASE_URL}/api/homework/${selectedHomeworkForSub.homework.id}/submit`, submissionData);
        
        toast("Homework submitted successfully!");
        setShowSubmitModal(false);
        setSubmissionFile(null);
        fetchHomeworks(selectedBatch, selectedStudent.email);
    } catch (err) {
        console.error(err);
        toast("Failed to submit homework.");
    } finally {
        setSubmitting(false);
    }
  };



  const dynamicMonthlyAttendance = () => {
    if (!selectedStudent) return "0%";
    const records = selectedStudent.attendanceRecords || [];
    const today = new Date();
    const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();
    const isFutureMonth = viewDate.getFullYear() > today.getFullYear() || (viewDate.getFullYear() === today.getFullYear() && viewDate.getMonth() > today.getMonth());
    
    let daysPassedInMonth = 0;
    if (isCurrentMonth) {
      daysPassedInMonth = today.getDate();
    } else if (!isFutureMonth) {
      daysPassedInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    }
    
    const presentDaysInMonth = records.filter(r => {
      if (r.status !== 'present') return false;
      const dateParts = r.date.split('-');
      if (dateParts.length < 3) return false;
      const rYear = parseInt(dateParts[0], 10);
      const rMonth = parseInt(dateParts[1], 10) - 1;
      return rYear === viewDate.getFullYear() && rMonth === viewDate.getMonth();
    }).length;

    // Fallback for legacy data if no new records found
    if (records.length === 0 && (selectedStudent.presentDays?.length || 0) > 0) {
        const legacyPresent = selectedStudent.presentDays.filter(d => {
            const dateParts = d.split('-');
            return parseInt(dateParts[0]) === viewDate.getFullYear() && (parseInt(dateParts[1]) - 1) === viewDate.getMonth();
        }).length;
        if (daysPassedInMonth === 0 || isFutureMonth) return "No Data";
        return `${Math.round((legacyPresent / daysPassedInMonth) * 100)}%`;
    }
    
    if (daysPassedInMonth === 0 || isFutureMonth) return "No Data";
    return `${Math.round((presentDaysInMonth / daysPassedInMonth) * 100)}%`;
  };

  const attendancePercent = dynamicMonthlyAttendance();

  const getDaysInMonth = () => {
    if (!selectedStudent) return [];
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const numDays = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: numDays }, (_, i) => {
      const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
      let status = 'none';
      let tooltip = '';
      
      const record = selectedStudent.attendanceRecords?.find(r => r.date === dayStr);
      if (record) {
        status = record.status;
        tooltip = `${record.status === 'present' ? 'Present' : 'Absent'}${record.timeIn ? `\nIn: ${record.timeIn}` : ''}${record.timeOut ? `\nOut: ${record.timeOut}` : ''}${record.remarks ? `\nNote: ${record.remarks}` : ''}`;
      } else {
        // Fallback to legacy lists if new records not found
        if (selectedStudent.presentDays?.includes(dayStr)) status = 'present';
        if (selectedStudent.absentDays?.includes(dayStr)) status = 'absent';
      }
      
      return { day: i + 1, status, tooltip };
    });
  };

  const getFirstDayOfMonth = () => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      return firstDay === 0 ? 6 : firstDay - 1;
  };

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.2); /* emerald-500/20 to match dashboard */
          border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.5);
        }
      `}</style>
      
      {/* Welcome Banner */}
      <div className="relative rounded-3xl bg-gradient-to-r from-emerald-900/40 via-teal-900/20 to-slate-900 border border-emerald-500/20 p-8 overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-[80px] rounded-full"></div>
        <div className="absolute bottom-0 left-32 w-48 h-48 bg-teal-500/10 blur-[60px] rounded-full"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1 max-w-2xl">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              Student Control Panel
            </h1>
            <div className="flex flex-col sm:flex-row gap-4">
              <select 
                className="w-full md:w-72 bg-slate-900/80 border border-emerald-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500 transition-colors shadow-inner disabled:opacity-50 disabled:cursor-wait"
                value={selectedBatch}
                onChange={e => {
                  setSelectedBatch(e.target.value);
                  setSelectedStudent(null);
                }}
                disabled={loadingBatches || isStudentRole}
              >
                <option value="">{loadingBatches ? "Loading Batches..." : "Select a Batch..."}</option>
                {batches.map(b => (
                  <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                ))}
              </select>
              
              <select 
                className="w-full md:w-80 bg-slate-900/80 border border-emerald-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-emerald-500 transition-colors shadow-inner font-medium disabled:opacity-50 disabled:cursor-wait"
                value={selectedStudent?.id || ''}
                onChange={e => setSelectedStudent(students.find(st => st.id.toString() === e.target.value) || null)}
                disabled={!selectedBatch || loadingStudents || isStudentRole}
              >
                <option value="">{selectedBatch ? (loadingStudents ? "Loading Students..." : "Select a Student...") : "Select a Batch first..."}</option>
                {Array.isArray(students) && students.map(st => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-3">
             {/* Admin tools moved to User Management */}
          </div>
        </div>
      </div>



      {!selectedStudent ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-emerald-500/20 rounded-3xl bg-slate-900/20 mt-8 relative overflow-hidden">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-500/10 blur-[100px] rounded-full"></div>
           <div className="relative z-10 flex flex-col items-center">
             <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center text-emerald-500/50 mb-6 shadow-inner ring-1 ring-white/5">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
             </div>
             <h3 className="text-2xl font-bold text-slate-300 mb-2">No Student Selected</h3>
             <p className="text-slate-500 text-center max-w-md">Choose a Batch and then drop down to select a specific enrolled student to view their exact realtime analytics & attendance data.</p>
           </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-700 text-left">
        
        {/* Left Column (Main Content) */}
        <div className="lg:col-span-2 space-y-8">
          


          {/* My Homework Section */}
          <section className="rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-indigo-950/20 to-slate-900/40 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[50px] rounded-full"></div>
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h2 className="text-xl font-semibold text-slate-200 flex items-center gap-2">
                <span className="text-indigo-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                </span>
                Assigned Homework
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
              {homeworks.length > 0 ? homeworks.map((hw, idx) => (
                <div key={idx} className="p-5 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-800/60 transition-all flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-slate-200">{hw.homework.title}</h3>
                      {hw.submitted ? (
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30 font-bold uppercase">Submitted</span>
                      ) : (
                        <span className="text-[10px] bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full border border-rose-500/30 font-bold uppercase">Pending</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-2 mb-3">{hw.homework.description || 'No instructions provided.'}</p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 font-medium mb-4">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      Due: {hw.homework.dueDate}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4 border-t border-slate-700/50 mt-auto">
                    {hw.attachment && (
                      <a 
                        href={`${API_BASE_URL}/api/files/download/${hw.attachment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-2 rounded-lg bg-slate-900 border border-slate-700 text-xs font-bold text-slate-300 hover:bg-slate-800 transition-colors text-center flex items-center justify-center gap-2"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        Resource
                      </a>
                    )}
                    <button 
                      onClick={() => {
                        setSelectedHomeworkForSub(hw);
                        setShowSubmitModal(true);
                      }}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${hw.submitted ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 hover:bg-indigo-600'}`}
                    >
                      {hw.submitted ? 'Update Work' : 'Submit Now'}
                    </button>
                  </div>
                </div>
              )) : (
                <div className="col-span-full py-8 text-center text-slate-500 italic">No homework assigned to your batch yet.</div>
              )}
            </div>
          </section>

          {/* Today's Live Timetable (Relocated) */}
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-1000 space-y-4">
             <div className="flex items-center gap-4 px-2">
                <button 
                  onClick={() => {
                    const d = new Date(timetableDate);
                    d.setDate(d.getDate() - 1);
                    setTimetableDate(d);
                  }}
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-all border border-slate-700/50 shadow-lg active:scale-90"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <h2 className="text-xl font-bold text-slate-100 min-w-max">
                  {timetableDate.toDateString() === new Date().toDateString() ? "Today's Schedule" : `Schedule for ${timetableDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                </h2>
                <button 
                  onClick={() => {
                    const d = new Date(timetableDate);
                    d.setDate(d.getDate() + 1);
                    setTimetableDate(d);
                  }}
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-all border border-slate-700/50 shadow-lg active:scale-90"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
             </div>
             <div className="max-h-[520px] overflow-y-auto custom-scrollbar pr-1">
                <UpcomingClasses 
                  hideHeader={true} 
                  initialBatch={selectedBatch} 
                  initialStudentId={selectedStudent?.id?.toString()} 
                  selectedDate={timetableDate}
                  hideDelete={true}
                />
             </div>
          </section>

          {/* Monthly Attendance Map */}
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden">
             <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/5 blur-[50px] rounded-full"></div>
             <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
                <div>
                   <h2 className="text-xl font-semibold text-slate-200 flex items-center gap-2">
                    <span className="text-emerald-400"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg></span>
                    Student Attendance Log
                     <span className="ml-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20">
                        Monthly: {attendancePercent}
                     </span>
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <button onClick={handlePrevMonth} className="p-1.5 hover:bg-slate-800 rounded-lg bg-slate-950 border border-slate-700/50 text-slate-400 transition-colors">
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </button>
                    <p className="text-slate-300 text-sm font-bold min-w-[120px] text-center bg-slate-950/50 py-1.5 px-3 rounded-lg shadow-inner">
                      {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </p>
                    <button onClick={handleNextMonth} className="p-1.5 hover:bg-slate-800 rounded-lg bg-slate-950 border border-slate-700/50 text-slate-400 transition-colors">
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </button>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-slate-800/50 border border-slate-700/50"></div><span className="text-xs text-slate-500 font-medium tracking-wide">None</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-emerald-500/20 border border-emerald-500/50"></div><span className="text-xs text-emerald-400 font-medium tracking-wide">Present</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-rose-500/20 border border-rose-500/50"></div><span className="text-xs text-rose-400 font-medium tracking-wide">Absent</span></div>
                </div>
              </div>
              
              <div className="grid grid-cols-7 gap-2 relative z-10">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">{day}</div>
                ))}
                
                {Array.from({ length: getFirstDayOfMonth() }).map((_, i) => <div key={`empty-${i}`} />)}
                
                {getDaysInMonth().map((d, i) => {
                  let colorClass = "bg-slate-950/30 border-slate-800 text-slate-600";
                  if (d.status === 'present') colorClass = "bg-emerald-500/20 border-emerald-500/50 text-emerald-400 font-bold shadow-[0_0_15px_rgba(16,185,129,0.1)]";
                  if (d.status === 'absent') colorClass = "bg-rose-500/20 border-rose-500/50 text-rose-400 font-bold shadow-[0_0_15px_rgba(244,63,94,0.1)]";
                  
                  return (
                    <div 
                      key={i} 
                      title={d.tooltip}
                      className={`aspect-[4/3] rounded-xl border flex items-center justify-center text-sm transition-all hover:scale-105 hover:bg-slate-800 cursor-help ${colorClass}`}
                    >
                      {d.day}
                    </div>
                  );
                })}
              </div>
          </section>






        </div>

        {/* Right Column (Sidebar) */}
        <div className="space-y-8">
          
          {/* PenSense Focus Metrics */}
          <section className="rounded-3xl border border-purple-500/20 bg-gradient-to-b from-purple-900/20 to-slate-900/40 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-full h-32 bg-purple-500/10 blur-[50px]"></div>
            
            <h2 className="text-sm font-bold uppercase tracking-widest text-purple-400 mb-6 relative z-10">PenSense Analytics</h2>
            
            <div className="space-y-4 relative z-10">
              <div className="p-4 rounded-2xl bg-slate-950/60 border border-purple-500/10 backdrop-blur">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                  </div>
                  <span className="text-sm font-medium text-slate-300">Writing Practice</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className={`text-2xl font-bold text-white ${penDataLoading ? 'animate-pulse opacity-50' : ''}`}>
                    {writingMetrics.wordsToday.toLocaleString()}
                  </span>
                  <span className="text-xs text-purple-400 font-medium mb-1">words today</span>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950/60 border border-purple-500/10 backdrop-blur">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  </div>
                  <span className="text-sm font-medium text-slate-300">Continuous Focus</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className={`text-2xl font-bold text-white ${penDataLoading ? 'animate-pulse opacity-50' : ''}`}>
                    {writingMetrics.avgSession}
                    <span className="text-sm font-normal text-slate-400">m</span>
                  </span>
                  <span className="text-xs text-indigo-400 font-medium mb-1">avg session</span>
                </div>
              </div>
            </div>
          </section>


          {/* Quick Revision Reminder */}
          <section className="rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white shadow-xl shadow-blue-500/20 relative overflow-hidden group">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
            <div className="relative z-10 flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
              </div>
              <div>
                <h3 className="font-bold mb-1 group-hover:translate-x-1 transition-transform">Revision Reminder</h3>
                <p className="text-sm text-blue-100 mb-3">You haven't revised Kinematics in 14 days. Time for a quick recap?</p>
                <button className="text-xs font-bold uppercase tracking-wider text-white border-b border-white/30 hover:border-white transition-colors pb-0.5">Start Quiz</button>
              </div>
            </div>
          </section>

        </div>
      </div>
      )}
      {/* Homework Submission Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200 text-left">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Submit Homework</h2>
                <p className="text-sm text-slate-400 mt-1">{selectedHomeworkForSub?.homework?.title}</p>
              </div>
              <button onClick={() => setShowSubmitModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <form className="space-y-6" onSubmit={handleSubmitHomework}>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Upload Submission PDF</label>
                <div className="relative group cursor-pointer h-32 w-full rounded-2xl border-2 border-dashed border-slate-700 hover:border-indigo-500 bg-slate-950/50 hover:bg-indigo-500/5 transition-colors flex flex-col items-center justify-center text-center p-4">
                  <input 
                    type="file" 
                    accept=".pdf" 
                    onChange={(e) => setSubmissionFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                  />
                  {!submissionFile ? (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 mb-2 group-hover:text-indigo-400"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                      <p className="text-xs text-slate-500 group-hover:text-indigo-400 transition-colors">Select your solved homework PDF</p>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400 mb-2"><path d="M20 6L9 17l-5-5"></path></svg>
                      <p className="text-xs text-emerald-400 font-medium truncate max-w-full">{submissionFile.name}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-indigo-500/5 border border-indigo-500/10 p-4 rounded-xl">
                <p className="text-[10px] text-slate-500 leading-relaxed uppercase font-bold tracking-wider mb-1">Important Note</p>
                <p className="text-xs text-slate-400 leading-relaxed">Only one submission is allowed. Re-submitting will overwrite your previous file.</p>
              </div>

              <button 
                type="submit" 
                disabled={submitting || !submissionFile}
                className="w-full py-4 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-bold transition-all shadow-lg shadow-indigo-500/20 flex justify-center items-center gap-2"
              >
                {submitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Upload & Submit'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
